import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { userHasCommerceRegistration } from '../../../utils/userCommerces.ts';
import styles from './CommerceDashboardBanner.module.scss';

const SESSION_HIDE_KEY = 'edify_commerce_dash_banner_hide';
const MIN_VIEWPORT_PX = 600;

interface CommerceDashboardBannerProps {
  userData: { skills?: unknown[]; id?: string | number } | null | undefined;
  conversationalActive?: boolean;
  commerceOnboardingEligible?: boolean;
}

const CommerceDashboardBanner: React.FC<CommerceDashboardBannerProps> = ({
  userData,
  conversationalActive,
  commerceOnboardingEligible,
}) => {
  const navigate = useNavigate();
  const [minViewport, setMinViewport] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= MIN_VIEWPORT_PX
  );
  const [hidden, setHidden] = useState(() =>
    typeof window !== 'undefined' ? sessionStorage.getItem(SESSION_HIDE_KEY) === '1' : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${MIN_VIEWPORT_PX}px)`);
    const apply = () => setMinViewport(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(SESSION_HIDE_KEY, '1');
    setHidden(true);
  }, []);

  const goToCommerceAlta = useCallback(() => {
    navigate('/comercios/alta');
  }, [navigate]);

  const hasCommerce = userHasCommerceRegistration(userData);
  const blockedBy = {
    conversationalActive: Boolean(conversationalActive),
    hidden,
    hasCommerce,
    notEligible: commerceOnboardingEligible !== true,
    minViewport: !minViewport,
  };

  if (
    blockedBy.conversationalActive ||
    blockedBy.hidden ||
    blockedBy.hasCommerce ||
    blockedBy.notEligible ||
    blockedBy.minViewport
  ) {
    return null;
  }

  return (
    <div className={styles.anchor}>
      <div className={styles.panel} role="region" aria-label="Registro de comercio">
        <button type="button" className={styles.closeButton} onClick={dismiss} aria-label="Cerrar aviso">
          <X size={18} strokeWidth={2} aria-hidden />
        </button>
        <p className={styles.description}>
          ¿Vendés materiales? Subí tu lista como la tengas, la organizamos y te avisamos cuando alguien necesite lo que
          vendés.
        </p>
        <div className={styles.actions}>
          <button type="button" className={styles.primaryAction} onClick={goToCommerceAlta}>
            Dar de alta mi comercio
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommerceDashboardBanner;
